import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CreditCard, Smartphone, Wallet, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useGuest } from '@/context/GuestContext';
import SupportTransparencyBlock from '@/components/support/SupportTransparencyBlock';
import ThankYouWall from '@/components/support/ThankYouWall';
import DonationThanksDialog from '@/components/support/DonationThanksDialog';
import { apiFetch } from '@/hooks/useFetch';

/**
 * "Support us" linktree — four payment channels for keeping
 * ClearCommunicationApp running.
 *
 * When the app is loaded inside a WebToNative wrapper (`?fromApp=1`
 * search param), every channel link routes through the external
 * browser via `window.WTN.openInExternalBrowser(url)` or
 * `window.open(url, '_system')` so Apple/Google/Amazon policy is
 * respected — payments must not be processed inside the in-app
 * webview for non-IAP transactions (donations + sponsored blog).
 *
 * Channels:
 *   - Stripe (Customer-chooses-price Payment Link)
 *   - CashApp ($CCADetectiveDino)
 *   - Venmo (@DetectiveDino)
 *   - PayPal (@DetectiveDino)
 */
const STRIPE_DONATION_URL = process.env.REACT_APP_STRIPE_DONATION_URL || '';

const CHANNELS = [
  {
    id: 'stripe',
    name: 'Stripe',
    handle: 'Card / Apple Pay / Google Pay',
    icon: CreditCard,
    color: 'bg-[#635BFF] hover:bg-[#4F47E0] text-white',
    href: STRIPE_DONATION_URL,
    notes: { en: 'Any amount, processed securely.', es: 'Cualquier cantidad, pago seguro.' },
  },
  {
    id: 'cashapp',
    name: 'CashApp',
    handle: '$CCADetectiveDino',
    icon: Smartphone,
    color: 'bg-[#00D632] hover:bg-[#00C12E] text-black',
    href: 'https://cash.app/$CCADetectiveDino',
    notes: { en: 'Mobile-friendly, instant in the US.', es: 'Móvil, instantáneo en EE.UU.' },
  },
  {
    id: 'venmo',
    name: 'Venmo',
    handle: '@DetectiveDino',
    icon: Wallet,
    color: 'bg-[#3D95CE] hover:bg-[#2E7AAE] text-white',
    href: 'https://venmo.com/u/DetectiveDino',
    notes: { en: 'US users only.', es: 'Solo en EE.UU.' },
  },
  {
    id: 'paypal',
    name: 'PayPal',
    handle: '@DetectiveDino',
    icon: Wallet,
    color: 'bg-[#0070BA] hover:bg-[#005EA6] text-white',
    href: 'https://paypal.me/DetectiveDino',
    notes: { en: 'International — works worldwide.', es: 'Internacional — funciona en todo el mundo.' },
  },
];

/**
 * isInApp — true when the WebToNative shell loaded us with
 * `?fromApp=1`. We persist the flag in sessionStorage so it
 * survives client-side navigations (the param only sticks on
 * the initial load).
 */
function isInApp() {
  try {
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    if (url.searchParams.get('fromApp') === '1') {
      sessionStorage.setItem('cca_from_app', '1');
      return true;
    }
    return sessionStorage.getItem('cca_from_app') === '1';
  } catch { return false; }
}

/**
 * Route to a payment link from the device's external browser. Apple,
 * Google, and Amazon all require this for non-IAP transactions —
 * opening the link inside the in-app webview is a rejectable offense.
 *
 * Tries (in order): WebToNative bridge → `window.open(url, '_system')`
 * (Cordova-style) → plain `_blank` (web fallback so the same code
 * path works on the desktop / mobile web).
 */
export function openInExternalBrowser(url) {
  if (!url) return;
  try {
    // 1. WebToNative bridge (preferred — guarantees external Safari/Chrome)
    if (typeof window !== 'undefined' && window.WTN?.openInExternalBrowser) {
      window.WTN.openInExternalBrowser(url);
      return;
    }
    // 2. Cordova-style external system browser
    if (typeof window !== 'undefined' && window.open) {
      const ref = window.open(url, '_system');
      if (ref) return;
    }
  } catch { /* fall through */ }
  // 3. Web fallback — new tab
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch { /* ignore */ }
}

const SupportPage = () => {
  const navigate = useNavigate();
  const { isSpanish } = useLanguage();
  const { isGuest } = useGuest?.() || {};
  // Runtime-fetched donation link (DB-backed, admin-editable) — the old
  // build-time env var vanished from prod builds and broke the tile.
  const [stripeUrl, setStripeUrl] = useState(STRIPE_DONATION_URL);
  // Auto-opens when the Stripe Payment Link redirects back with ?donated=1
  const [thanksOpen, setThanksOpen] = useState(false);

  useEffect(() => {
    apiFetch('/api/support/config').then(({ data }) => {
      if (data?.stripe_donation_url) setStripeUrl(data.stripe_donation_url);
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('donated') === '1') {
      setThanksOpen(true);
      // Strip the param so a refresh doesn't re-trigger the celebration.
      params.delete('donated');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  const channels = CHANNELS.map((c) =>
    c.id === 'stripe' ? { ...c, href: stripeUrl } : c
  );
  const inApp = isInApp();

  const handleChannelClick = (e, channel) => {
    if (!channel.href) {
      e.preventDefault();
      return;
    }
    // In-app — ALWAYS route to external browser to stay store-compliant.
    if (inApp) {
      e.preventDefault();
      openInExternalBrowser(channel.href);
    }
    // Otherwise let the <a target=_blank> default behaviour run.
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Header />
      <section className="py-8 sm:py-12">
        <div className="container max-w-2xl">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-2 -ml-2"
            data-testid="support-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {isSpanish ? 'Atrás' : 'Back'}
          </Button>

          <div className="text-center mb-6">
            <div className="text-6xl mb-2" aria-hidden="true">🌳⭐</div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              {isSpanish
                ? 'Apóyanos'
                : 'Support us'}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-md mx-auto">
              {isSpanish
                ? '¿Disfrutas ClearCommunicationApp? Considera ayudarnos a mantenerlo funcionando. ¡Compra una estrella de árbol para que el Detective Dino la coma — como los longnecks!'
                : 'Enjoy ClearCommunicationApp? Consider supporting Detective Dino to keep it running. Buy Detective Dino a tree star to eat (just like Longnecks eat)!'}
            </p>
            {inApp && (
              <p
                className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1 bg-muted/40 rounded-full px-3 py-1"
                data-testid="support-external-banner"
              >
                <ExternalLink className="h-3 w-3" />
                {isSpanish
                  ? 'Los enlaces se abren en tu navegador externo'
                  : 'Links open in your external browser'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="support-channels">
            {channels.map((c) => {
              const Icon = c.icon;
              const disabled = !c.href;
              return (
                <a
                  key={c.id}
                  href={c.href || '#'}
                  target={!inApp && c.href ? '_blank' : undefined}
                  rel={!inApp && c.href ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleChannelClick(e, c)}
                  data-testid={`support-${c.id}`}
                  aria-disabled={disabled}
                  className={`rounded-2xl p-4 sm:p-5 shadow-md transition-all ${
                    disabled
                      ? 'opacity-60 cursor-not-allowed bg-muted text-muted-foreground'
                      : c.color + ' hover:shadow-lg active:scale-[0.98]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-black/15 p-2 shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg flex items-center gap-1.5">
                        {c.name}
                        {!disabled && <ExternalLink className="h-3.5 w-3.5 opacity-70" />}
                      </div>
                      <div className="text-sm font-mono opacity-90 truncate">
                        {c.handle}
                      </div>
                      <div className="text-xs mt-1 opacity-80">
                        {isSpanish ? c.notes.es : c.notes.en}
                      </div>
                      {disabled && c.id === 'stripe' && (
                        <div className="text-[11px] mt-1.5 italic">
                          {isSpanish
                            ? 'Próximamente — admin configurando el enlace.'
                            : 'Coming soon — admin is finalizing the link.'}
                        </div>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <Card className="mt-6 border border-dashed">
            <CardHeader>
              <CardTitle className="text-base">
                {isSpanish ? '¿A dónde van las donaciones?' : 'Where do donations go?'}
              </CardTitle>
              <CardDescription>
                {isSpanish
                  ? 'Costos de servidor, almacenamiento de audio, claves API (ElevenLabs, OpenAI), té chai para escribir blogs y frases — ¡y una nueva habilidad para los Dinos de vez en cuando!'
                  : 'Server costs, audio storage, API keys (ElevenLabs, OpenAI), chai tea for writing blogs and phrases — and a new skill for Dinos every now and then!'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">
              {isSpanish
                ? 'ClearCommunicationApp LLC es un negocio pequeño dirigido por una mamá neurodivergente de 4 niños neurodivergentes. Cada dólar ayuda a mantener la app gratuita y sin publicidad para los suscriptores VIP.'
                : 'ClearCommunicationApp LLC is a small business run by a neurodivergent mom of 4 neurodivergent kids. Every dollar helps keep the app free and ad-free for VIP supporters.'}
            </CardContent>
          </Card>

          <SupportTransparencyBlock />
          <ThankYouWall />

          {!isGuest && (
            <p className="text-center text-[11px] text-muted-foreground mt-4">
              {isSpanish
                ? 'Las donaciones no son reembolsables y no incluyen acceso a funciones VIP. Para los beneficios VIP, '
                : 'Donations are non-refundable and do not include VIP features. For VIP perks, '}
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="underline hover:text-foreground"
                data-testid="support-vip-link"
              >
                {isSpanish ? 'consulta los planes' : 'see our plans'}
              </button>.
            </p>
          )}
        </div>
      </section>

      <DonationThanksDialog
        open={thanksOpen}
        onOpenChange={setThanksOpen}
        isSpanish={isSpanish}
      />
    </div>
  );
};

export default SupportPage;
